import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient, useQuery } from "@apollo/react-hooks";

import Moment from "moment";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";

// import components
import { Banner, Spinner } from "lib/components";

// import query
import { GET_ORDER_LINES, GET_PAYMENT_EXPORT_SETTING } from "app/payments/apollo/queries";
import { GET_WHOLESALE_SETTING } from "app/product/apollo/queries";

// import hooks
import { useBatchExport } from "lib/hooks";

// import helpers
import { baseHelper } from "lib/helpers";
import paymentHelper from "./helper/payment";

const Export = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);
  const isVendor = baseHelper.isVendor(currentUser);

  const { data: getSettingData, loading: settingLoading, error: settingError } = useQuery(GET_PAYMENT_EXPORT_SETTING);
  const { loading: wholesaleLoading } = useQuery(GET_WHOLESALE_SETTING);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_ORDER_LINES,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getOrderLines;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("common.message.error.noPayment"));
          }
          const { orderLines = [], hasMore = false, totalCount } = response.data || {};
          return { rows: orderLines, hasMore, totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback(
    (rows) => {
      const settingResponse = baseHelper.getResponseData(getSettingData, constant.gql.GET_PAYMENT_EXPORT_SETTING);
      return paymentHelper.paymentCSVData(rows, settingResponse, true, false, isVendor);
    },
    [getSettingData, isVendor]
  );

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
    return `${constant.CONSOLIDATED_INVOICE} - ${date}.csv`;
  }, []);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("common.message.error.noPayment")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("common.message.error.noPayment"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    const settingsLoading = settingLoading || wholesaleLoading;
    if (settingsLoading || hasStartedRef.current) {
      return;
    }
    if (settingError) {
      showErrorBanner(settingError.message || cms("common.message.error.noPayment"));
      return;
    }

    hasStartedRef.current = true;
    const {
      accountingStatus,
      endDate,
      fulfillmentStatus,
      startDate,
      status,
      taxStatus,
      vendor,
    } = baseHelper.queryParamsFromLocation(data);
    const filters = {};
    if (status && status.length) {
      filters.status = status.split(",");
    }
    if (fulfillmentStatus && fulfillmentStatus.length) {
      filters.fulfillmentStatus = fulfillmentStatus.split(",");
    }
    if (vendor && vendor.length) {
      filters.vendor = vendor.split(",");
    }
    if (accountingStatus && accountingStatus.length) {
      filters.accountingStatus = accountingStatus.split(",");
    }
    if (startDate && endDate) {
      filters.dateRange = { start: startDate, end: endDate };
    }
    if (taxStatus && taxStatus.length) {
      filters.taxStatus = taxStatus.split(",");
    }
    start(filters);
  }, [data, start, cms, showErrorBanner, settingLoading, wholesaleLoading, settingError]);

  return (
    <Layout.Section>
      {wholesaleLoading && <Spinner isFullPage />}
      {exportStatus === "loading" && (
        <div className="fullPageProgress">
          <ProgressBar progress={progress} size="medium" />
          <br />
          <TextStyle variation="subdued">
            {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
          </TextStyle>
        </div>
      )}
      {banner.isOpen && (
        <Banner
          title={banner.title}
          status={banner.status}
          isOpen={banner.isOpen}
          onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
        />
      )}
    </Layout.Section>
  );
};

export default withFeature(withErrorBoundary(Export), { feature: constant.EXPORT_PAYMENT });
